import { useState } from "react";
import { CloseOutlined, DownOutlined } from "@ant-design/icons";
import { Button, Upload, UploadFile, Dropdown, MenuProps } from "antd";
import clsx from "clsx";
import { Control, FieldValues, UseFormResetField, UseFormSetValue } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { DocumentFileLink } from "@entities/journal";
import { GenericDocumentModal, DocumentItem } from "../../generic-document-modal";

interface IDocumentSelector {
  label: string;
  accept: Array<string>;
  control: Control;
  name: string;
  className?: string;
  buttonClassName?: string;
  labelClassName?: string;
  triggerButtonLabel?: string;
  resetField: UseFormResetField<FieldValues>;
  isAddFromOldService?: boolean;
  setValue?: UseFormSetValue<FieldValues>;
  onDocumentSelect?: (document: DocumentItem) => void;
  onDocumentRemove?: () => void;
}

export const DocumentSelector = ({
  label,
  accept,
  control,
  name,
  className,
  buttonClassName,
  labelClassName,
  triggerButtonLabel,
  resetField,
  isAddFromOldService = false,
  setValue,
  onDocumentSelect,
  onDocumentRemove,
}: IDocumentSelector) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const onRemove = () => {
    resetField(name);
    setFileList([]);

    if (onDocumentRemove) {
      onDocumentRemove();
    }
  };

  const handleBeforeUpload = (file: any) => {
    const uploadFile: UploadFile = {
      uid: file.uid || `rc-upload-${Date.now()}`,
      name: file.name,
      status: "done",
      size: file.size,
      type: file.type,
      originFileObj: file,
    };

    setFileList([uploadFile]);

    if (setValue) {
      setValue(name, { fileList: [uploadFile] }, { shouldValidate: true });
    }

    return false;
  };

  const handleDocumentSelect = (document: DocumentItem | null) => {
    if (document) {
      const fakeFileBlob = new File([], document.title, { type: "application/pdf" });

      const fakeFile: UploadFile = {
        uid: document.key,
        name: document.title,
        status: "done",
        url: document.originalData?.documentUrl,
        size: 0,
        type: "application/pdf",
        originFileObj: fakeFileBlob as any,
      };

      setFileList([fakeFile]);

      if (setValue && isAddFromOldService) {
        setValue(name, { fileList: [fakeFile] }, { shouldValidate: true });
      }

      if (onDocumentSelect) {
        onDocumentSelect(document);
      }
    }
    setIsModalVisible(false);
  };

  const menuItems: MenuProps["items"] = [
    {
      key: "add-document",
      label: "Выбрать документ",
      onClick: () => setIsModalVisible(true),
    },
    {
      key: "upload",
      label: (
        <Upload
          multiple={false}
          showUploadList={false}
          accept={accept?.filter(Boolean).join(",")}
          beforeUpload={handleBeforeUpload}
          fileList={fileList}
        >
          Загрузить документ
        </Upload>
      ),
    },
  ];

  if (!fileList.length) {
    return (
      <div className={clsx("document-selector-container", className)}>
        <div className="document-selector-button">
          <div
            className={clsx("document-selector-button-title", labelClassName)}
          >
            {label}
          </div>
          <FormItem
            control={control}
            name={name}
            style={{
              margin: 0,
            }}
          >
            {isAddFromOldService ? (
              <Dropdown menu={{ items: menuItems }} trigger={["hover"]}>
                <Button
                  className={clsx(buttonClassName)}
                  type="default"
                  style={{ backgroundColor: "rgba(234, 238, 248, 1)" }}
                >
                  {triggerButtonLabel ?? "Выбрать документ"} <DownOutlined />
                </Button>
              </Dropdown>
            ) : (
              <Upload
                multiple={false}
                showUploadList={false}
                accept={accept?.filter(Boolean).join(",")}
                beforeUpload={handleBeforeUpload}
                fileList={fileList}
              >
                <Button
                  className={clsx(buttonClassName)}
                  type="default"
                  style={{ backgroundColor: "rgba(234, 238, 248, 1)" }}
                >
                  Загрузить
                </Button>
              </Upload>
            )}
          </FormItem>
        </div>
        {isAddFromOldService && (
          <GenericDocumentModal
            visible={isModalVisible}
            onCancel={() => setIsModalVisible(false)}
            onConfirm={handleDocumentSelect}
            docType="REGULATION_DOC"
            endpoint="permits"
            usePost={false}
            singleSelect
            title="Выберите документ"
          />
        )}
      </div>
    );
  }

  return (
    <div className={clsx("document-selector-container", className)}>
      <div className="document-selector-button">
        <DocumentFileLink
          className="document-selector-button-title link"
          fileName={fileList[0]?.name ?? "document.pdf"}
          documentUrl={
            fileList[0]?.url && !fileList[0].url.startsWith("blob:")
              ? fileList[0].url
              : undefined
          }
          blobUrl={
            fileList[0]?.url?.startsWith("blob:")
              ? fileList[0].url
              : undefined
          }
          localFile={
            (fileList[0]?.originFileObj as File | undefined)?.size
              ? (fileList[0]?.originFileObj as File)
              : undefined
          }
        />
        <CloseOutlined onClick={onRemove} className="close-icon" />
      </div>
    </div>
  );
};
